import { useCallback, useEffect, useState } from 'react';
import ApiService from '../../services/api.service';

const pick = (obj, camelKey, snakeKey, fallback = 0) => {
  if (!obj) return fallback;
  const value = obj[camelKey] !== undefined ? obj[camelKey] : obj[snakeKey];
  return value !== undefined && value !== null ? value : fallback;
};

const normalizeCenterMetrics = (rows = []) => rows.reduce((acc, row) => {
  const typingCenterId = pick(row, 'typingCenterId', 'typing_center_id', '');
  if (!typingCenterId) return acc;

  acc[typingCenterId] = {
    totalWork: Number(pick(row, 'totalWork', 'total_work')),
    invoicedWork: Number(pick(row, 'invoicedWork', 'invoiced_work')),
    inProgressWork: Number(pick(row, 'inProgressWork', 'in_progress_work')),
    completedWork: Number(pick(row, 'completedWork', 'completed_work'))
  };
  return acc;
}, {});

const normalizeCenter = (center = {}) => ({
  id: center.id,
  name: center.name,
  code: center.code,
  active: center.active,
  address: center.address,
  email: center.email,
  logoUrl: pick(center, 'logoUrl', 'logo_url', ''),
  contactPerson: pick(center, 'contactPerson', 'contact_person', ''),
  contactNumber: pick(center, 'contactNumber', 'contact_number', ''),
  organizationType: pick(center, 'organizationType', 'organization_type', ''),
  createdAt: pick(center, 'createdAt', 'created_at', ''),
  createdBy: pick(center, 'createdBy', 'created_by', null)
});

export const useGroupCenters = ({ loadCenters = true } = {}) => {
  const [centers, setCenters] = useState([]);
  const [centerMetrics, setCenterMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [opening, setOpening] = useState(null);

  useEffect(() => {
    let active = true;

    if (!loadCenters) {
      setLoading(false);
      return undefined;
    }

    const loadCentersData = async () => {
      try {
        const [centersRes, summaryRes] = await Promise.all([
          ApiService.getGroupCenters(),
          ApiService.getGroupDashboardSummary()
        ]);
        const list = (centersRes.data?.data || []).map(normalizeCenter);
        const performance = summaryRes.data?.data?.centerPerformance || summaryRes.data?.data?.center_performance || [];
        if (active) {
          setCenters(list);
          setCenterMetrics(normalizeCenterMetrics(performance));
        }
      } catch (err) {
        if (active) setError(err.response?.data?.message || 'Failed to load your typing centers.');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadCentersData();

    return () => {
      active = false;
    };
  }, [loadCenters]);

  const openCenter = useCallback(async (tc) => {
    setError('');
    setOpening(tc.id);

    try {
      const res = await ApiService.groupSwitch(tc.id);
      const data = res.data?.data;
      if (!data?.token) throw new Error('No token returned');

      // Enter the TC as an acting TC_ADMIN session. Persist the acting token
      // and user as the main-app auth, keep the group token so the owner can
      // return, then hard-navigate so App re-seeds its auth state cleanly.
      localStorage.setItem('jwt_token', data.token);
      localStorage.setItem('currentUser', JSON.stringify(data.user));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('actingAsGroupOwner', 'true');
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.response?.data?.message || 'Could not open this typing center.');
      setOpening(null);
    }
  }, []);

  return {
    centers,
    centerMetrics,
    loading,
    error,
    opening,
    openCenter
  };
};
